"use client";
// @ts-strict-ignore
import React from "react";

import { Col, Row } from "components/layout/Grid";
import {
  BackToGeminiSubNav,
  HeaderBodyContainer,
  MobileHeaderBodyContainer,
  HeaderContainer,
  NavRowItem,
  BackToGeminiSubNavInner
} from "./style";
import NextLink from "components/parts/NextLink";
import { HTMLButton } from "components/parts/button";
import { usePathname } from "i18n/routing";
import { scroller } from "react-scroll";
import MobileSearch from "./MobileSearch";
import MobileSideNav from "./MobileSideNav";
import DesktopSearch from "./DesktopSearch";
import { Configure, InstantSearch } from "react-instantsearch-dom";
import algoliasearch from "algoliasearch/lite";
import { wrappedAlgoliaClient } from "utils/algolia";
import { ALGOLIA_API_KEY, ALGOLIA_ID, EXCHANGE_BASE_URL } from "config/index";
import { FramerLazyMotion } from "components/site/FramerLazyMotion";
import { Button, Flex, Menu, SearchInput, Text } from "@hubble/web";
import { web } from "@hubble/tokens";
import { IconChevronDownSmall, IconMenu } from "@hubble/icons";
import NextImage from "components/parts/NextImage";
import { mixpanelClient } from "analytics/mixpanel";
import RiskBanner from "../../navigation/banners/risk-banner";
import { PromoBanner } from "./PromoBanner";
import { useMeasure } from "react-use";

const HeaderBody = ({
  cryptopediaCategories
}: {
  cryptopediaCategories?: string[];
}) => {
  const [riskBannerSizeRef, { height: riskBannerHeight }] = useMeasure();

  const [isSearchOpen, setIsSearchOpen] = React.useState(false);
  const menuTriggerRef = React.useRef(null);

  const handleTopicNavigation = React.useCallback((category: string) => {
    const targetId = category.toLowerCase();
    scroller.scrollTo(targetId, {
      duration: 700,
      smooth: true,
      offset: -100
    });
  }, []);

  React.useEffect(() => {
    const menuTrigger = menuTriggerRef.current;
    if (!menuTrigger) return;

    const triggerPointerDownEvent = () => {
      // Create a new pointerdown event
      const pointerDownEvent = new PointerEvent("pointerdown", {
        bubbles: true,
        cancelable: true,
        composed: true,
        pointerId: 1,
        pointerType: "mouse"
      });

      // Dispatch the pointerdown event on the element
      menuTrigger.dispatchEvent(pointerDownEvent);
    };

    menuTrigger.addEventListener("mouseenter", triggerPointerDownEvent);

    return () => {
      menuTrigger.removeEventListener("mouseenter", triggerPointerDownEvent);
    };
  }, []);

  return (
    <React.Fragment>
      <HeaderBodyContainer>
        <BackToGeminiSubNav>
          <BackToGeminiSubNavInner>
            <Flex alignItems="center">
              <Text.Body size="xs" bold marketing>
                Powered by
              </Text.Body>
              &nbsp;
              <NextImage
                src="https://cdn.builder.io/api/v1/image/assets/1b77ce3a269a43e985e77f3d65f715ba/68b0cd0b74104a889a538aa680da9a58"
                alt="Gemini Logo"
                width={55}
                height={12}
                onClick={() => {
                  mixpanelClient.track(
                    "Cryptopedia subnav - Gemini logo clicked"
                  );
                  window.open("https://www.gemini.com", "_blank");
                }}
              />
            </Flex>
            <PromoBanner />
            <NextLink
              onClick={() => {
                mixpanelClient.track(
                  "Cryptopedia subnav - Gemini Sign in clicked"
                );
              }}
              href={`${EXCHANGE_BASE_URL}/signin`}
              target="_blank"
              id="cryptopedia-header-signin"
            >
              <Text.Body size="xs" bold marketing>
                Sign in
              </Text.Body>
            </NextLink>
          </BackToGeminiSubNavInner>
          <div ref={riskBannerSizeRef}>
            <RiskBanner />
          </div>
        </BackToGeminiSubNav>
        <Row
          alignItems="center"
          css={`
            margin-top: calc(3.5rem + ${riskBannerHeight}px);
            margin-bottom: 16px;
          `}
        >
          <Col md={7}>
            <Text.Heading fontWeight={700} size="sm" marketing>
              <NextLink href="/cryptopedia" raw>
                Cryptopedia.{" "}
              </NextLink>
              <span
                style={{
                  color: web.color.base.gray[500]
                }}
              >
                Your trusted source for all things crypto.
              </span>
            </Text.Heading>{" "}
          </Col>
          <Col md={5} justifyContent="flex-end" alignItems="center" gap="1rem">
            <DesktopSearch
              isOpen={isSearchOpen}
              toggleOpen={() =>
                setIsSearchOpen((isSearchOpen) => !isSearchOpen)
              }
            />
            <NavRowItem>
              <Menu>
                <Menu.Trigger ref={menuTriggerRef}>
                  <Button.Tertiary
                    rightElement={<IconChevronDownSmall />}
                    aria-label="Topics"
                  >
                    Topics
                  </Button.Tertiary>
                </Menu.Trigger>
                <Menu.Content>
                  {cryptopediaCategories?.map((category, index) => (
                    <Menu.Item
                      key={index}
                      label={category}
                      onClick={() => handleTopicNavigation(category)}
                    />
                  ))}
                </Menu.Content>
              </Menu>
            </NavRowItem>
            <NavRowItem>
              <Button.Primary
                href={`${EXCHANGE_BASE_URL}/register`}
                target="_blank"
                size="md"
                style={{ width: "max-content" }}
                onClick={() => {
                  mixpanelClient.track("Cryptopedia nav - Buy Crypto clicked");
                }}
              >
                <Text.Body size="sm" bold color="inherit">
                  Buy crypto
                </Text.Body>
              </Button.Primary>
            </NavRowItem>
          </Col>
        </Row>
      </HeaderBodyContainer>
    </React.Fragment>
  );
};

const MobileHeaderBody = () => {
  const [isMenuActive, setIsMenuActive] = React.useState(false);
  const [isSearchOpen, setIsSearchOpen] = React.useState(false);
  const pathname = usePathname();

  React.useEffect(() => {
    setIsMenuActive(false);
  }, [pathname]);

  return (
    <React.Fragment>
      <MobileHeaderBodyContainer smSpacing={16}>
        <Flex
          borderBottom="1px solid #BFBFC6"
          pb={1.5}
          mb={2}
          mt={1.5}
          justifyContent="space-between"
          alignItems="center"
        >
          <NextLink href="/cryptopedia">
            <Text.Heading
              style={{
                fontWeight: 700
              }}
              size="sm"
              marketing
            >
              Cryptopedia.{" "}
            </Text.Heading>
          </NextLink>{" "}
          <HTMLButton onClick={() => setIsMenuActive(true)}>
            <IconMenu />
          </HTMLButton>
        </Flex>
        <SearchInput
          width="100%"
          onClick={(e) => {
            e.stopPropagation();
            setIsSearchOpen(true);
          }}
          mb={2}
          placeholder="Search"
          label="Search"
          readOnly
        />
        <RiskBanner />
      </MobileHeaderBodyContainer>
      <MobileSideNav
        isOpen={isMenuActive}
        onClose={() => setIsMenuActive(false)}
      />
      <MobileSearch
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
      />
    </React.Fragment>
  );
};

const algoliaClient = algoliasearch(ALGOLIA_ID, ALGOLIA_API_KEY);
const searchClient = wrappedAlgoliaClient(algoliaClient);

const INDEX_NAME = "prod_cryptopedia_glossary";
const CryptopediaHeader = (props) => {
  return (
    <InstantSearch
      searchClient={searchClient}
      resultsState={props.resultsState}
      indexName={INDEX_NAME}
      {...props}
    >
      <FramerLazyMotion>
        <Configure hitsPerPage={12} />
        <PromoBanner variant="mobile" />
        <HeaderContainer as="header">
          <HeaderBody cryptopediaCategories={props.cryptopediaCategories} />
          <MobileHeaderBody />
        </HeaderContainer>
      </FramerLazyMotion>
    </InstantSearch>
  );
};

export default CryptopediaHeader;
